import {
  CheckCircle,
  Trash,
  ArrowCounterClockwise,
  ArrowsClockwise,
  PencilSimple,
  XCircle,
  Pause,
  CurrencyCircleDollar,
  Eye,
  WhatsappLogo,
  Lightning,
} from '@phosphor-icons/react';
import { getCategoryIcon, getPriorityColor, formatAmount, formatDate, isOverdue, recurrenceLabel } from './taskHelpers';

const SubscriptionStatusBadge = ({ status }) => {
  if (!status) return null;
  const config = {
    cancelled: { bg: 'bg-[#fecdd3]', text: 'text-[#9f1239]', icon: <XCircle size={10} weight="fill" />, label: 'Cancelled' },
    suspended: { bg: 'bg-[#fef3c7]', text: 'text-[#92400e]', icon: <Pause size={10} weight="fill" />, label: 'Suspended' },
    active: { bg: 'bg-[#d1fae5]', text: 'text-[#065f46]', icon: <CheckCircle size={10} weight="fill" />, label: 'Active' },
  };
  const c = config[status];
  if (!c) return null;
  return (
    <span className={`inline-flex items-center gap-1 ml-2 mt-2 px-2 py-0.5 rounded-full ${c.bg} ${c.text} text-[10px] font-bold uppercase tracking-wider`} data-testid={`sub-status-badge`}>
      {c.icon} {c.label}
    </span>
  );
};

const PaymentStatusBadge = ({ status }) => {
  if (!status) return null;
  if (status === 'paid') {
    return (
      <span className="inline-flex items-center gap-1 ml-2 mt-2 px-2 py-0.5 rounded-full bg-[#a3f5b8] text-[#0f6b3a] text-[10px] font-bold uppercase tracking-wider" data-testid="payment-status-badge">
        <CurrencyCircleDollar size={10} weight="fill" /> Paid
      </span>
    );
  }
  return null;
};

export const TaskItem = ({ task, onComplete, onUndo, onEdit, onDelete }) => {
  const isCompleted = task.status === 'completed';
  const isCancelled = task.status === 'cancelled' || task.subscription_status === 'cancelled';

  return (
    <div
      className={`grid grid-cols-1 sm:grid-cols-12 gap-4 p-5 transition-colors ${
        isCancelled ? 'bg-[#fef2f2] hover:bg-[#fee2e2]' :
        isCompleted ? 'bg-[#f9faf9] hover:bg-[#f2f5f2]' : 'hover:bg-[#f9f9fb]'
      }`}
      data-testid={`task-item-${task.task_id}`}
    >
      {/* Task Details */}
      <div className="sm:col-span-5 flex items-start gap-3">
        <div className="mt-1">
          {isCompleted
            ? <CheckCircle size={20} className="text-[#0f6b3a]" weight="fill" />
            : getCategoryIcon(task.category)
          }
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className={`font-semibold ${isCancelled ? 'text-[#9ca3af]' : isCompleted ? 'text-[#596065]' : 'text-[#2d3338]'}`}>
              {task.title}
            </p>
            {isCancelled && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#fecdd3] text-[#9f1239] text-[10px] font-bold uppercase tracking-wider" data-testid={`cancelled-badge-${task.task_id}`}>
                <XCircle size={10} weight="fill" />
                Cancelled
              </span>
            )}
            {!isCancelled && isCompleted && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#a3f5b8] text-[#0f6b3a] text-[10px] font-bold uppercase tracking-wider" data-testid={`paid-badge-${task.task_id}`}>
                <CheckCircle size={10} weight="fill" />
                {task.category === 'bill' || task.category === 'subscription' ? 'Paid' : 'Done'}
              </span>
            )}
          </div>
          <p className="text-sm text-[#596065] truncate">{task.provider || task.description}</p>
          <div className="flex flex-wrap items-center">
            <span className={`inline-block mt-2 tag category-${task.category}`}>
              {task.category}
            </span>
            {task.recurrence && task.recurrence !== 'none' && (
              <span className="inline-flex items-center gap-1 ml-2 mt-2 px-2 py-0.5 rounded-full bg-[#e0f2fe] text-[#0369a1] text-[10px] font-bold uppercase tracking-wider" data-testid={`recurrence-badge-${task.task_id}`}>
                <ArrowsClockwise size={10} weight="bold" />
                {recurrenceLabel(task.recurrence)}
              </span>
            )}
            {task.subscription_status && !isCancelled && (
              <SubscriptionStatusBadge status={task.subscription_status} />
            )}
            {task.payment_status && !isCompleted && (
              <PaymentStatusBadge status={task.payment_status} />
            )}
            {(task.source === 'whatsapp_agent' || task.needs_review) && task.status === 'pending' && (
              <span className="inline-flex items-center gap-1 ml-2 mt-2 px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e] text-[10px] font-bold uppercase tracking-wider" data-testid={`review-badge-${task.task_id}`}>
                <Eye size={10} weight="fill" /> For Review
              </span>
            )}
          </div>
          {/* Source metadata for agent-created tasks */}
          {task.source === 'whatsapp_agent' && (task.source_detail || task.created_by_agent) && (
            <div className="flex items-center gap-1.5 mt-1">
              <WhatsappLogo size={10} className="text-[#25d366]" weight="fill" />
              <span className="text-[10px] text-[#acb3b8]">
                {task.source_detail && `${task.source_detail}`}
                {task.source_detail && task.created_by_agent && ' · '}
                {task.created_by_agent && <><Lightning size={8} className="inline text-[#f59e0b]" /> {task.created_by_agent}</>}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Due Date */}
      <div className="sm:col-span-2 flex items-center">
        <span className={`text-sm font-medium ${
          isCompleted
            ? 'text-[#acb3b8]'
            : isOverdue(task.due_date) && task.status === 'pending'
              ? 'text-[#ba1a1a]'
              : 'text-[#596065]'
        }`}>
          {!isCompleted && isOverdue(task.due_date) && task.status === 'pending' && (
            <span className="tag tag-error text-xs mr-2">OVERDUE</span>
          )}
          {formatDate(task.due_date)}
        </span>
      </div>

      {/* Amount */}
      <div className="sm:col-span-2 flex items-center">
        <span className={`font-bold ${isCompleted ? 'text-[#acb3b8]' : 'text-[#2d3338]'}`}>
          {formatAmount(task.amount, task.currency)}
        </span>
      </div>

      {/* Priority */}
      <div className="sm:col-span-2 flex items-center gap-2">
        {isCompleted ? (
          <div className="w-10 h-10 rounded-xl bg-[#a3f5b8] text-[#0f6b3a] flex items-center justify-center">
            <CheckCircle size={18} weight="fill" />
          </div>
        ) : (
          <div className={`w-10 h-10 rounded-xl ${getPriorityColor(task.priority)} flex items-center justify-center font-bold text-sm`}>
            {task.priority}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="sm:col-span-1 flex items-center justify-end gap-1">
        {isCompleted ? (
          <button
            onClick={() => onUndo(task.task_id)}
            className="p-2 hover:bg-[#d6e3ff] rounded-lg transition-colors"
            title="Undo"
            data-testid={`undo-task-${task.task_id}`}
          >
            <ArrowCounterClockwise size={18} className="text-[#005bc1]" />
          </button>
        ) : (
          <>
            <button
              onClick={() => onComplete(task.task_id)}
              className="p-2 hover:bg-[#a3f5b8] rounded-lg transition-colors"
              title={task.category === 'appointment' ? 'Mark done' : 'Mark as paid'}
              data-testid={`complete-task-${task.task_id}`}
            >
              <CheckCircle size={20} className="text-[#0f6b3a]" weight="duotone" />
            </button>
            <button
              onClick={() => onEdit(task)}
              className="p-2 hover:bg-[#e0f2fe] rounded-lg transition-colors"
              title="Edit"
              data-testid={`edit-task-${task.task_id}`}
            >
              <PencilSimple size={18} className="text-[#0369a1]" />
            </button>
          </>
        )}
        <button
          onClick={() => onDelete(task.task_id)}
          className="p-2 hover:bg-[#ff8b9a] rounded-lg transition-colors"
          title="Delete"
          data-testid={`delete-task-${task.task_id}`}
        >
          <Trash size={18} className="text-[#ba1a1a]" weight="duotone" />
        </button>
      </div>
    </div>
  );
};
